import { useCallback, useMemo } from 'react';
import cn from 'clsx';
import { type SearchListing } from '@pkgs/api';
import { Wrapper } from '@ev-tech/ui-core-components/Wrapper';
import { Button } from '@ev-tech/ui-core-components/Button';

import { Grid } from '@/components/property-expose/Grid';
import { SearchResultCardWrapper } from '@/components/common/SearchResultCardWrapper/SearchResultCardWrapper';

import { Slide, Slider } from '../Slider';
import { Intro } from '../Intro';

import styles from './PropertyPreview.module.css';

interface PropertyPreviewProps {
  className?: string;
  intro: {
    overline?: string;
    headline: string;
    copy?: string;
    verticalLineTop?: boolean;
  };
  sliderControls: {
    previous: string;
    next: string;
  };
  hasBackgroundColor?: boolean;
  isSlider?: boolean;
  maxNumberProperties?: number;
  properties: SearchListing[];
  button: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  fallBackImage?: string;
  onCardClick?: (index: number) => void;
  'data-testid'?: string;
}

export const PropertyPreview = ({
  className,
  intro,
  hasBackgroundColor = false,
  isSlider = false,
  maxNumberProperties = 9,
  properties,
  sliderControls,
  button,
  onCardClick,
  'data-testid': dataTestId,
}: PropertyPreviewProps) => {
  const { overline, headline, copy, verticalLineTop = false } = intro;

  const mapPropertyToListingCard = useCallback(
    (property: SearchListing, index: number) => {
      const listingCard = (
        <SearchResultCardWrapper
          key={property.id}
          listingResult={{ listing: property }}
          onClick={() => {
            onCardClick?.(index);
          }}
        />
      );

      return isSlider ? (
        <Slide key={property.id} className={styles.slide}>
          {listingCard}
        </Slide>
      ) : (
        <div className={styles.listingCard}>{listingCard}</div>
      );
    },
    [isSlider, onCardClick],
  );

  const propertyCards = useMemo(() => {
    return properties.slice(0, maxNumberProperties).map(mapPropertyToListingCard);
  }, [mapPropertyToListingCard, maxNumberProperties, properties]);

  const propertyComponent = isSlider ? (
    <Slider
      slidesPerView={{ mobile: 1.15, tabletPortrait: 2.25, laptop: 3 }}
      scrollSlides={{ mobile: 1, tabletPortrait: 2, laptop: 3 }}
      controlLabels={sliderControls}
    >
      {propertyCards}
    </Slider>
  ) : (
    <Grid>{propertyCards}</Grid>
  );

  return (
    <section className={cn(styles.baseClass, className)} data-has-background-color={hasBackgroundColor} data-testid={dataTestId}>
      <Wrapper
        className={styles.wrapper}
        data-has-background-color={hasBackgroundColor}
        smallerBottomSpace
        width={{ laptop: 'l', tabletLandscape: 'l' }}
      >
        <Intro
          headline={{ label: headline, size: 'h3', as: 'h2' }}
          copy={copy}
          subtitle={overline}
          verticalLineTop={verticalLineTop}
          nested={true}
        />
        {propertyComponent}
        {Boolean(button.label) && <Button className={styles.button} variant="tertiary" {...button} />}
      </Wrapper>
    </section>
  );
};
